import React from "react";
import { AlertTriangle, CameraOff, ImageOff, UserX, Users } from "lucide-react";
import type {
    EmployeeEnrollment,
    EnrollmentFailureReason,
    EnrollmentState,
} from "../interfaces/SelfieEnrollment";

/**
 * Enrollment Status (TAS-568) — the single copy source for why an enrollment
 * photo failed and what to do about it. Ported from the design bundle's
 * `enroll/enroll-data.js → ENROLL_REASONS`. The modal, the Selfie Exceptions
 * Enrollment view, the roster callout / list modal and the CSV all read it.
 *
 * The same taxonomy is classified from `LastError` in the API
 * (`employeeEnrollmentStateUtil.classifyEnrollmentError`) and in
 * SyncAppRiceServices' failure email — keep the three in step.
 */

/** Reuses the Selfie Exceptions page's reason-family classes. */
export type EnrollmentReasonCls = "r-noface" | "r-mismatch" | "r-error";

/** Only `firstName` is needed by the copy; wider person shapes pass straight in. */
export interface EnrollmentPerson {
    firstName: string;
    lastName?: string;
}

export interface EnrollmentReasonMeta {
    label: string;
    /** Family line under the chip (the exceptions page's family labels). */
    family: string;
    cls: EnrollmentReasonCls;
    /** Swatch for the "Why they failed" bar and legend. */
    dot: string;
    Icon: React.ComponentType<{ className?: string }>;
    /** Which footer / row action is primary for this reason. */
    emphasis: "replace" | "retry";
    /** "What happened" sentence. */
    say: (employee: EnrollmentPerson, enrollment: EmployeeEnrollment) => React.ReactNode;
    /** "What to do" sentence. */
    doit: (employee: EnrollmentPerson) => React.ReactNode;
}

/** 0–1 → "87%". */
export function pctLabel(confidence: number | null | undefined): string {
    if (confidence == null || Number.isNaN(confidence)) return "—";
    return `${Math.round(confidence * 100)}%`;
}

export const ENROLL_REASON_ORDER: EnrollmentFailureReason[] = [
    "multiple_faces",
    "no_face",
    "duplicate",
    "photo_missing",
    "system",
];

export const ENROLL_REASONS: Record<EnrollmentFailureReason, EnrollmentReasonMeta> = {
    multiple_faces: {
        label: "More than one face",
        family: "Photo quality",
        cls: "r-noface",
        dot: "#d4881a",
        Icon: Users,
        emphasis: "replace",
        say: (e) => (
            <>
                Azure found <b>more than one face</b> in this photo, so it can’t tell which one is {e.firstName}.
            </>
        ),
        doit: (e) => (
            <>
                <b>Crop or retake the photo</b> so only {e.firstName} is in the frame. No crew shots, reflections or
                people in the background.
            </>
        ),
    },
    no_face: {
        label: "No face found",
        family: "Photo quality",
        cls: "r-noface",
        dot: "#d4881a",
        Icon: CameraOff,
        emphasis: "replace",
        say: () => (
            <>
                Azure <b>couldn’t find a face</b> in this photo.
            </>
        ),
        doit: (e) => (
            <>
                <b>Retake the photo</b> with {e.firstName} facing the camera in good light. No sunglasses, mask or hat
                brim.
            </>
        ),
    },
    duplicate: {
        label: "Looks like another employee",
        family: "Possible identity issue",
        cls: "r-mismatch",
        dot: "#e0533f",
        Icon: UserX,
        emphasis: "replace",
        say: (_e, n) => {
            const m = n.matchedEmployee;
            const at =
                n.confidence != null ? (
                    <>
                        , at <b>{pctLabel(n.confidence)}</b>
                    </>
                ) : null;
            return m ? (
                <>
                    This photo matches{" "}
                    <b>
                        {m.firstName} {m.lastName}
                    </b>{" "}
                    ({m.altEmployeeID}), who is already enrolled{at}. Anything over 80% is treated as the same person.
                </>
            ) : (
                <>
                    This photo matches <b>another employee</b> who is already enrolled{at}. Anything over 80% is treated
                    as the same person.
                </>
            );
        },
        doit: () => (
            <>
                <b>Compare the two photos.</b> If it’s one person with two records, delete or replace the photo on the
                wrong record, then retry this one. If they’re different people, retake this photo. If it keeps
                matching, call support.
            </>
        ),
    },
    photo_missing: {
        label: "Photo file missing",
        family: "System issue",
        cls: "r-error",
        dot: "#6b7686",
        Icon: ImageOff,
        emphasis: "replace",
        say: () => (
            <>
                The photo file was <b>gone by the time the check ran</b>. This usually happens when the photo is
                replaced or deleted within a few minutes of uploading.
            </>
        ),
        doit: () => <b>Upload the photo again.</b>,
    },
    system: {
        label: "Couldn’t finish",
        family: "System issue",
        cls: "r-error",
        dot: "#6b7686",
        Icon: AlertTriangle,
        emphasis: "retry",
        say: () => (
            <>
                The check <b>stopped partway through on our side</b>. As far as we can tell, the photo itself is fine.
            </>
        ),
        doit: () => (
            <>
                <b>Retry.</b> If it fails again, call support and give them the technical detail below.
            </>
        ),
    },
};

/** Unknown / missing reasons render as `system` — never raw snake_case. */
export function enrollmentReasonKey(reason: string | null | undefined): EnrollmentFailureReason {
    return reason && reason in ENROLL_REASONS ? (reason as EnrollmentFailureReason) : "system";
}

export function enrollmentReason(reason: string | null | undefined): EnrollmentReasonMeta {
    return ENROLL_REASONS[enrollmentReasonKey(reason)];
}

/** Curly apostrophes out, for CSV / clipboard text. */
export function plainText(s: string): string {
    return s.replace(/[‘’]/g, "'");
}

/** One-line status for exports: "Enrollment failed: More than one face" · "Needs a photo" · "Verifying" · "On file". */
export function enrollmentStatusText(state: EnrollmentState, enrollment?: EmployeeEnrollment | null): string {
    switch (state) {
        case "failed":
            return `Enrollment failed: ${plainText(enrollmentReason(enrollment?.reason).label)}`;
        case "verifying":
            return "Verifying";
        case "enrolled":
            return "On file";
        default:
            return "Needs a photo";
    }
}

/* ── time formatting (viewer-local; the API sends UTC ISO) ─────────────────── */

function parse(iso: string | null | undefined): Date | null {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** "1:48 PM" */
export function fmtTime(iso: string | null | undefined): string {
    const d = parse(iso);
    return d ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "—";
}

/** "Oct 7, 2025" */
export function fmtDate(iso: string | null | undefined): string {
    const d = parse(iso);
    return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
}

/** Today → "1:48 PM"; otherwise "Sep 25, 4:02 PM". */
export function fmtWhen(iso: string | null | undefined, now: Date = new Date()): string {
    const d = parse(iso);
    if (!d) return "—";
    if (d.toDateString() === now.toDateString()) return fmtTime(iso);
    return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${fmtTime(iso)}`;
}

/** "Sep 25, 2026 · 4:02 PM" */
export function fmtWhenLong(iso: string | null | undefined): string {
    const d = parse(iso);
    return d ? `${fmtDate(iso)} · ${fmtTime(iso)}` : "—";
}

/** "just now" · "29 min ago" · "3h ago" · "1 day ago" · "8 days ago". */
export function fmtAgo(iso: string | null | undefined, now: Date = new Date()): string {
    const d = parse(iso);
    if (!d) return "";
    const m = Math.round((now.getTime() - d.getTime()) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.round(h / 24);
    return days === 1 ? "1 day ago" : `${days} days ago`;
}
